import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AdminSection, writeAdminRoute } from '../components/AdminNav.jsx';
import { useTranslation } from '../hooks/useTranslation.js';
import { requestJson } from '../utils/authClient.js';
import { formatBerlin } from './VeridionShared.jsx';

// The company memory as a picture (IDD D-HP-ADMIN-031): a force-directed graph drawn in plain SVG, without a
// library, fed by the Core's read-only graph door through the admin API. An admin starts from a search hit, the
// newest memories or the work items, clicks a node to read it, expands its neighbours, and reads the facts of an
// entity with the time they hold. Beside it the three stores of the memory with what each holds (D-HP-ADMIN-033).
const API = '/api/admin/memory';
const WIDTH = 1200;
const HEIGHT = 640;
const NODE_RADIUS = 9;
const LABEL_LENGTH = 34;
const SIMULATION_TICKS = 360;

const formatInteger = (value) => (value === null || value === undefined || value === '' ? '-' : Number(value).toLocaleString());
const formatBytes = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = Number(value);
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) { size /= 1024; unit += 1; }
  return `${size.toFixed(unit ? 1 : 0)} ${units[unit]}`;
};
const formatTime = formatBerlin;
const short = (value, length = LABEL_LENGTH) => {
  const text = String(value ?? '');
  return text.length > length ? `${text.slice(0, length - 1)}…` : text;
};

// One colour per label, fixed for the labels the memory is built from and derived for every other label, so a
// picture reads the same way every time it is drawn.
const LABEL_HUES = {
  Memory: 210, Entity: 30, Project: 140, System: 275, Customer: 350, Person: 190, Position: 50, Decision: 0,
  Commitment: 95, Risk: 15, Meeting: 320, Task: 170, WorkItem: 245, Meaning: 260, CodingTask: 120, MergeRequest: 80,
  Commit: 65, Branch: 105, Repository: 155, Module: 180, SonarRule: 330, VeridionAgent: 225, Definition: 290,
  DefinitionVersion: 300, Scope: 40, Document: 20, Episodic: 0,
  DomainCluster: 165, KnowledgeTopic: 205, ResearchPublication: 280,
};
const hueOf = (label) => {
  if (LABEL_HUES[label] !== undefined) return LABEL_HUES[label];
  let hash = 0;
  for (const char of String(label || '')) hash = (hash * 31 + char.charCodeAt(0)) % 360;
  return hash;
};
const colourOf = (label) => (label === 'Episodic' ? 'hsl(0 0% 55%)' : `hsl(${hueOf(label)} 55% 45%)`);

// A section of the page: the shared section with its state in the header (IDD D-HP-ADMIN-040).
const Section = ({ id = '', title, subtitle, children, actions = null, count = null, collapsed = false }) => (
  <AdminSection id={id} title={title} subtitle={subtitle} actions={actions} count={count} collapsed={collapsed}>{children}</AdminSection>
);

const Table = ({ columns, rows, empty }) => (
  <div className="admin-table-shell"><div className="admin-table-scroll">
    <table className="admin-table">
      <thead><tr>{columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr></thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={row.id || index}>{columns.map((column) => <td key={column.key} className="break-words">{column.render ? column.render(row) : (row[column.key] ?? '-')}</td>)}</tr>
        ))}
        {!rows.length ? <tr><td colSpan={columns.length}>{empty}</td></tr> : null}
      </tbody>
    </table>
  </div></div>
);

// -- the stores -----------------------------------------------------------------------------------------------------

export const MemoryStores = () => {
  const { t } = useTranslation();
  const m = t.admin.memory;
  const g = m.graph;
  const [stores, setStores] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    requestJson(`${API}/stores`).then((data) => { setStores(data); setError(''); }).catch((failure) => setError(failure?.code || 'unavailable'));
  }, []);

  const neo4j = stores?.neo4j?.data;
  const postgres = stores?.postgres?.data;
  const qdrant = stores?.qdrant?.data;
  const partError = (part) => (part?.error ? <div className="text-sm text-error">{m.unavailable} ({part.error})</div> : null);

  return (
    <Section title={g.storesTitle} subtitle={g.storesSubtitle}>
      {error ? <div className="homepage-muted-panel p-4 text-sm text-error">{m.unavailable} ({error})</div> : null}
      {!stores && !error ? <div className="text-sm text-text-secondary">{m.loading}</div> : null}
      {stores ? (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="homepage-muted-panel p-4 space-y-3">
            <h3 className="text-lg font-bold">{g.storeNeo4j}</h3>
            <p className="site-copy text-sm">{g.storeNeo4jHolds}</p>
            {partError(stores.neo4j)}
            {neo4j ? (
              <>
                <div className="text-sm"><strong>{formatInteger(neo4j.nodes)}</strong> {g.nodes} · <strong>{formatInteger(neo4j.relationships)}</strong> {g.relationships}</div>
                <Table columns={[{ key: 'type', label: g.relationshipType }, { key: 'count', label: m.count, render: (row) => formatInteger(row.count) }]}
                  rows={(neo4j.relationshipTypes || []).map((row) => ({ ...row, id: row.type }))} empty={m.empty} />
              </>
            ) : null}
          </div>
          <div className="homepage-muted-panel p-4 space-y-3">
            <h3 className="text-lg font-bold">{g.storePostgres}</h3>
            <p className="site-copy text-sm">{g.storePostgresHolds}</p>
            {partError(stores.postgres)}
            {postgres ? (
              <>
                <div className="text-sm"><strong>{formatInteger((postgres.tables || []).length)}</strong> {g.tables} · <strong>{formatInteger((postgres.tables || []).reduce((sum, row) => sum + (row.rows || 0), 0))}</strong> {g.rows} · {formatBytes(postgres.sizeBytes)}</div>
                {postgres.error ? <div className="text-sm text-error">{m.unavailable} ({postgres.error})</div> : null}
                <Table columns={[
                  { key: 'name', label: g.table },
                  { key: 'rows', label: g.rows, render: (row) => formatInteger(row.rows) },
                  { key: 'purpose', label: g.purpose },
                ]} rows={(postgres.tables || []).map((row) => ({ ...row, id: row.name }))} empty={m.empty} />
              </>
            ) : null}
          </div>
          <div className="homepage-muted-panel p-4 space-y-3">
            <h3 className="text-lg font-bold">{g.storeQdrant}</h3>
            <p className="site-copy text-sm">{g.storeQdrantHolds}</p>
            {partError(stores.qdrant)}
            {qdrant ? (
              <>
                <div className="text-sm"><strong>{formatInteger(qdrant.documents)}</strong> {g.documents} · {g.session}: {qdrant.session || '-'} · {qdrant.available ? m.ready : m.notReady}</div>
                <Table columns={[{ key: 'name', label: g.collection }, { key: 'role', label: g.role }]}
                  rows={(qdrant.collections || []).map((name) => ({
                    id: name,
                    name,
                    role: name === qdrant.documentsCollection ? g.roleDocuments : name === qdrant.curatedCollection ? g.roleCurated : '-',
                  }))} empty={m.empty} />
              </>
            ) : null}
          </div>
        </div>
      ) : null}

    </Section>
  );
};

// -- the picture ----------------------------------------------------------------------------------------------------

// A force layout over the picture's nodes: repulsion between every two nodes, springs along the relationships,
// a pull to the centre, run for a bounded number of ticks after every change. Positions live outside React's state
// and are written back once per animation frame.
const simulate = (nodes, edges, positions, ticks, onFrame) => {
  const ids = nodes.map((node) => node.id);
  const index = new Map(ids.map((id, i) => [id, i]));
  const links = edges.filter((edge) => index.has(edge.source) && index.has(edge.target))
    .map((edge) => [index.get(edge.source), index.get(edge.target)]);
  const points = ids.map((id, i) => positions.get(id) || {
    x: WIDTH / 2 + Math.cos(i * 2.4) * (40 + i * 3), y: HEIGHT / 2 + Math.sin(i * 2.4) * (40 + i * 3), vx: 0, vy: 0, fixed: false,
  });
  ids.forEach((id, i) => positions.set(id, points[i]));
  let tick = 0;
  let frame = 0;
  const step = () => {
    const alpha = Math.max(0.02, 1 - tick / ticks);
    for (let i = 0; i < points.length; i += 1) {
      for (let j = i + 1; j < points.length; j += 1) {
        const dx = points[j].x - points[i].x;
        const dy = points[j].y - points[i].y;
        const distance = Math.max(12, Math.hypot(dx, dy));
        const force = (2400 * alpha) / (distance * distance);
        const fx = (dx / distance) * force;
        const fy = (dy / distance) * force;
        points[i].vx -= fx; points[i].vy -= fy;
        points[j].vx += fx; points[j].vy += fy;
      }
    }
    links.forEach(([a, b]) => {
      const dx = points[b].x - points[a].x;
      const dy = points[b].y - points[a].y;
      const distance = Math.max(1, Math.hypot(dx, dy));
      const force = (distance - 110) * 0.04 * alpha;
      const fx = (dx / distance) * force;
      const fy = (dy / distance) * force;
      points[a].vx += fx; points[a].vy += fy;
      points[b].vx -= fx; points[b].vy -= fy;
    });
    points.forEach((point) => {
      point.vx += (WIDTH / 2 - point.x) * 0.004 * alpha;
      point.vy += (HEIGHT / 2 - point.y) * 0.004 * alpha;
      if (!point.fixed) {
        point.x = Math.min(WIDTH - 20, Math.max(20, point.x + point.vx));
        point.y = Math.min(HEIGHT - 20, Math.max(20, point.y + point.vy));
      }
      point.vx *= 0.82; point.vy *= 0.82;
    });
    tick += 1;
    onFrame();
    if (tick < ticks) frame = window.requestAnimationFrame(step);
  };
  frame = window.requestAnimationFrame(step);
  return () => window.cancelAnimationFrame(frame);
};

const mergeGraph = (current, incoming) => {
  const nodes = new Map((current?.nodes || []).map((node) => [node.id, node]));
  const edges = new Map((current?.edges || []).map((edge) => [edge.id, edge]));
  (incoming?.nodes || []).forEach((node) => nodes.set(node.id, node));
  (incoming?.edges || []).forEach((edge) => edges.set(edge.id, edge));
  return { nodes: Array.from(nodes.values()), edges: Array.from(edges.values()) };
};

const PropertyTable = ({ props, g }) => (
  <Table columns={[
    { key: 'key', label: g.property },
    { key: 'value', label: g.value, render: (row) => <span style={{ whiteSpace: 'pre-wrap' }}>{typeof row.value === 'string' ? row.value : JSON.stringify(row.value)}</span> },
  ]} rows={Object.entries(props || {}).map(([key, value]) => ({ id: key, key, value }))} empty={g.noProperties} />
);

export const MemoryGraph = () => {
  const { t, currentLang } = useTranslation();
  const isDe = currentLang === 'de';
  const pickField = (row, field) => (isDe ? row?.[field] : (row?.[`${field}_en`] || row?.[field]));
  const m = t.admin.memory;
  const g = m.graph;
  const [graph, setGraph] = useState({ nodes: [], edges: [] });
  const [truncated, setTruncated] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [facts, setFacts] = useState(null);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState(null);
  const [newest, setNewest] = useState([]);
  const [work, setWork] = useState([]);
  const [workStatus, setWorkStatus] = useState('');
  const [notice, setNotice] = useState({ message: '', error: false });
  const [busy, setBusy] = useState(false);
  const [, setFrame] = useState(0);
  const positions = useRef(new Map());
  const stopRef = useRef(null);
  const svgRef = useRef(null);
  const drag = useRef(null);
  const [view, setView] = useState({ x: 0, y: 0, k: 1 });

  const fail = (failure) => setNotice({ message: `${m.failed} (${failure?.code || failure?.status || '-'})`, error: true });

  const run = useCallback(async (work) => {
    setBusy(true);
    try {
      await work();
      setNotice({ message: '', error: false });
    } catch (failure) {
      fail(failure);
    } finally {
      setBusy(false);
    }
  }, []);

  // Every change of the picture starts the layout again; a node already placed keeps its place.
  useEffect(() => {
    if (stopRef.current) stopRef.current();
    const known = new Set(graph.nodes.map((node) => node.id));
    Array.from(positions.current.keys()).forEach((id) => { if (!known.has(id)) positions.current.delete(id); });
    if (!graph.nodes.length) return undefined;
    stopRef.current = simulate(graph.nodes, graph.edges, positions.current, SIMULATION_TICKS, () => setFrame((frame) => frame + 1));
    return () => { if (stopRef.current) stopRef.current(); };
  }, [graph]);

  // The address may name a work item as a third segment (#company-memory/graph/wi-…), handed over by the day
  // section: the picture then opens on that work item instead of the newest memories.
  const focusOf = () => {
    const [, , focus = ''] = String(window.location.hash || '').replace(/^#/, '').split('/');
    return decodeURIComponent(focus);
  };
  const [focus, setFocus] = useState(() => focusOf());
  useEffect(() => {
    const follow = () => setFocus(focusOf());
    window.addEventListener('hashchange', follow);
    return () => window.removeEventListener('hashchange', follow);
  }, []);

  useEffect(() => {
    run(async () => {
      if (focus) {
        const found = await requestJson(`${API}/graph/search?q=${encodeURIComponent(focus)}&limit=25`);
        const items = found.items || [];
        const hit =
          items.find((item) => item.label === 'DomainCluster' && (item.props?.bu_code === focus || item.props?.id === focus || item.props?.key === focus)) ||
          items.find((item) => item.label === 'WorkItem' && item.props?.id === focus) ||
          items.find((item) => item.label === 'DomainCluster') ||
          items[0];
        if (hit) {
          const depth = hit.label === 'DomainCluster' ? 2 : 1;
          const data = await requestJson(`${API}/graph/neighbours?id=${encodeURIComponent(hit.id)}&depth=${depth}&limit=80`);
          positions.current.clear();
          setGraph({ nodes: data.nodes || [], edges: data.edges || [] });
          setTruncated(Boolean(data.truncated));
          setSelected(hit.id);
          setDetail(await requestJson(`${API}/graph/node?id=${encodeURIComponent(hit.id)}`));
          if (hit.label === 'DomainCluster') {
            setActiveClusterId(hit.props?.id || hit.props?.key || '');
            setQuery(hit.props?.bu_code || focus);
          }
          setNotice({ message: '', error: false });
          return;
        }
        setNotice({ message: `${g.noHits} (${focus})`, error: true });
      }
      const data = await requestJson(`${API}/graph/recent?limit=30`);
      setNewest(data.items || []);
      setGraph({ nodes: data.nodes || [], edges: data.edges || [] });
      setTruncated(false);
    });
    requestJson(`${API}/graph/work-items?limit=20`).then((data) => setWork(data.items || [])).catch(() => setWork([]));
  }, [run, focus]);

  const show = (data, replace = true) => {
    setGraph((current) => (replace ? { nodes: data.nodes || [], edges: data.edges || [] } : mergeGraph(current, data)));
    setTruncated(Boolean(data.truncated));
  };

  const select = (id) => {
    setSelected(id);
    setFacts(null);
    setDetail(null);
    if (!id) return;
    run(async () => {
      const node = await requestJson(`${API}/graph/node?id=${encodeURIComponent(id)}`);
      setDetail(node);
    });
  };

  const expand = (id, depth = 1) => run(async () => {
    const data = await requestJson(`${API}/graph/neighbours?id=${encodeURIComponent(id)}&depth=${depth}&limit=60`);
    show(data, false);
    if (data.truncated) setTruncated(true);
  });

  const centre = (id, depth = 1) => run(async () => {
    const data = await requestJson(`${API}/graph/neighbours?id=${encodeURIComponent(id)}&depth=${depth}&limit=80`);
    positions.current.clear();
    show(data, true);
    select(id);
  });

  const loadFacts = (id) => run(async () => {
    const data = await requestJson(`${API}/graph/facts?id=${encodeURIComponent(id)}&limit=60`);
    setFacts(data);
    show(data, false);
  });

  const loadNewest = () => run(async () => {
    const data = await requestJson(`${API}/graph/recent?limit=30`);
    setNewest(data.items || []);
    positions.current.clear();
    show(data, true);
    setSelected(null); setDetail(null); setFacts(null);
  });

  const loadWork = (status = workStatus) => run(async () => {
    const data = await requestJson(`${API}/graph/work-items?limit=20&status=${encodeURIComponent(status)}`);
    setWork(data.items || []);
    positions.current.clear();
    show(data, true);
    setSelected(null); setDetail(null); setFacts(null);
  });

  const search = (event) => {
    event.preventDefault();
    if (!query.trim()) return;
    run(async () => {
      const data = await requestJson(`${API}/graph/search?q=${encodeURIComponent(query)}&limit=20`);
      setHits(data.items || []);
    });
  };

  const labels = useMemo(() => {
    const counts = new Map();
    graph.nodes.forEach((node) => counts.set(node.label, (counts.get(node.label) || 0) + 1));
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [graph]);

  const nodeById = useMemo(() => new Map(graph.nodes.map((node) => [node.id, node])), [graph]);
  const point = (id) => positions.current.get(id);

  // Dragging a node pins it where it is dropped; dragging the background pans; the wheel zooms.
  const toGraph = (event) => {
    const box = svgRef.current.getBoundingClientRect();
    const x = ((event.clientX - box.left) / box.width) * WIDTH;
    const y = ((event.clientY - box.top) / box.height) * HEIGHT;
    return { x: (x - view.x) / view.k, y: (y - view.y) / view.k };
  };
  const onPointerDown = (event, id) => {
    event.stopPropagation();
    const at = toGraph(event);
    drag.current = id ? { id, moved: false } : { pan: true, startX: event.clientX, startY: event.clientY, view };
    if (id) {
      const p = point(id);
      if (p) { p.fixed = true; drag.current.dx = p.x - at.x; drag.current.dy = p.y - at.y; }
    }
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const onPointerMove = (event) => {
    if (!drag.current) return;
    if (drag.current.pan) {
      const box = svgRef.current.getBoundingClientRect();
      const scale = WIDTH / box.width;
      setView({ ...drag.current.view, x: drag.current.view.x + (event.clientX - drag.current.startX) * scale, y: drag.current.view.y + (event.clientY - drag.current.startY) * scale });
      return;
    }
    const p = point(drag.current.id);
    if (!p) return;
    const at = toGraph(event);
    p.x = at.x + drag.current.dx; p.y = at.y + drag.current.dy;
    drag.current.moved = true;
    setFrame((frame) => frame + 1);
  };
  const onPointerUp = () => {
    if (drag.current && !drag.current.pan && !drag.current.moved) select(drag.current.id);
    drag.current = null;
  };
  const onWheel = (event) => {
    const factor = event.deltaY < 0 ? 1.15 : 1 / 1.15;
    setView((current) => ({ ...current, k: Math.min(4, Math.max(0.3, current.k * factor)) }));
  };

  const showEdgeLabels = graph.edges.length <= 80;
  const selectedNode = selected ? nodeById.get(selected) : null;
  const isEntity = (node) => Boolean(node && (node.labels || []).includes('Entity'));
  const showButton = (id, label = g.show) => <button type="button" className="homepage-button-secondary" onClick={() => centre(id)}>{label}</button>;
  const [domainsData, setDomainsData] = useState(null);
  const [activeClusterId, setActiveClusterId] = useState('');

  useEffect(() => {
    requestJson('/api/admin/veridion-core/memory-bubbles').then(setDomainsData).catch(() => setDomainsData(null));
  }, []);

  // The memory-bubbles payload: seed-labeled domain rows with live Neo4j counts (`counts_live`
  // false means the zeros are honest), plus the live research-publication rows (D-VCORE-333 §5).
  const clusters = (domainsData?.bubbles || []).map((c) => ({ ...c, id: c.id || c.bubble_id }));
  const researchPapers = (domainsData?.quantum_research_space?.papers || []).map((p) => ({ ...p, id: p.pub_id }));
  const selectedClusterObj = clusters.find((c) => c.id === activeClusterId) || null;

  const focusCluster = (cluster) => {
    if (!cluster) {
      setActiveClusterId('');
      setQuery('');
      setHits(null);
      writeAdminRoute('company-memory', 'graph', '');
      loadNewest();
      return;
    }
    const cid = cluster.id || cluster.bubble_id;
    setActiveClusterId(cid);
    setQuery(cluster.bu_code);
    writeAdminRoute('company-memory', 'graph', cluster.bu_code);
    run(async () => {
      const found = await requestJson(`${API}/graph/search?q=${encodeURIComponent(cluster.bu_code)}&limit=25`);
      const items = found.items || [];
      setHits(items);
      const hub =
        items.find((item) => item.label === 'DomainCluster' && (item.props?.bu_code === cluster.bu_code || item.props?.id === cid)) ||
        items.find((item) => item.label === 'DomainCluster') ||
        items[0];
      if (hub) {
        const hubId = hub.id;
        const [data, nodeDetail] = await Promise.all([
          requestJson(`${API}/graph/neighbours?id=${encodeURIComponent(hubId)}&depth=2&limit=80`),
          requestJson(`${API}/graph/node?id=${encodeURIComponent(hubId)}`).catch(() => null),
        ]);
        positions.current.clear();
        show(data, true);
        setSelected(hubId);
        setFacts(null);
        setDetail(nodeDetail);
      }
    });
  };

  return (
    <>
      <Section title={g.title} subtitle={g.subtitle} actions={(
        <div className="flex flex-wrap gap-2">
          <button type="button" className="homepage-button-secondary" onClick={() => focusCluster(null)} disabled={busy}>{g.startNewest}</button>
          <button type="button" className="homepage-button-secondary" onClick={() => loadWork()} disabled={busy}>{g.startWork}</button>
        </div>
      )}>
        {clusters.length ? (
          <div className="homepage-muted-panel p-4 space-y-3">
            <div className="font-bold text-sm">{g.clusterPickTitle}</div>
            <div className="text-xs text-text-secondary">{g.clusterPickHint}</div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={!activeClusterId ? 'homepage-button-primary text-xs' : 'homepage-button-secondary text-xs'}
                onClick={() => focusCluster(null)}
              >
                {g.allClusters}
              </button>
              {clusters.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={activeClusterId === c.id ? 'homepage-button-primary text-xs' : 'homepage-button-secondary text-xs'}
                  onClick={() => focusCluster(c)}
                >
                  {c.bu_code} · {isDe ? (c.german_name || c.name) : c.name}
                </button>
              ))}
            </div>
            {selectedClusterObj ? (
              <div className="site-card p-3 text-xs space-y-2">
                <div className="font-bold text-sm">
                  {g.activeCluster}: {selectedClusterObj.bu_code} — {isDe ? (selectedClusterObj.german_name || selectedClusterObj.name) : selectedClusterObj.name}
                </div>
                <div>
                  <strong>{m.clusterPolicy}:</strong> {pickField(selectedClusterObj, 'isolation_policy')}
                </div>
                <div>
                  <strong>{m.clusterRoles}:</strong> {(selectedClusterObj.lead_positions || []).join(', ')}
                  {' · '}<strong>{m.clusterBridges}:</strong> {(selectedClusterObj.cross_bubble_bridges || []).join(', ')}
                  {' · '}<strong>{m.clusterCounts}:</strong>{' '}
                  {selectedClusterObj.counts_live === false
                    ? m.countsUnavailable
                    : `${selectedClusterObj.node_count} / ${selectedClusterObj.edge_count}`}
                </div>
                <ul className="list-disc pl-5 space-y-1">
                  {(pickField(selectedClusterObj, 'recent_insights') || []).map((ins, idx) => <li key={idx}>{ins}</li>)}
                </ul>
                {(selectedClusterObj.id === 'bu-quantum' || selectedClusterObj.id === 'bu-quantum-research-space') && researchPapers.length ? (
                  <div className="pt-2 space-y-1">
                    <div className="font-bold">{g.publicationsTitle}</div>
                    <ul className="list-disc pl-5 space-y-1">
                      {researchPapers.map((p) => (
                        <li key={p.id}>
                          <strong>{isDe ? p.title : (p.title_en || p.title)}</strong>: {pickField(p, 'key_mechanism')}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        <form className="flex flex-wrap gap-2" onSubmit={search}>
          <input className="form-input flex-1 min-w-0" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={g.searchPlaceholder} required />
          <button type="submit" className="homepage-button-primary" disabled={busy}>{g.find}</button>
        </form>
        {notice.message ? <div className={`homepage-muted-panel p-3 text-sm ${notice.error ? 'text-error' : ''}`}>{notice.message}</div> : null}
        {hits ? (
          <Table rows={hits} empty={g.noHits} columns={[
            { key: 'label', label: m.label },
            { key: 'name', label: g.name },
            { key: 'source', label: m.source, render: (row) => g.sources?.[row.source] || row.source },
            { key: 'show', label: '', render: (row) => showButton(row.id) },
          ]} />
        ) : null}
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-2">
            <div className="text-sm text-text-secondary">
              {g.shown.replace('{nodes}', formatInteger(graph.nodes.length)).replace('{edges}', formatInteger(graph.edges.length))}
              {truncated ? ` · ${g.truncated}` : ''}
              {busy ? ` · ${m.loading}` : ''}
            </div>
            <div className="homepage-muted-panel" style={{ overflow: 'hidden' }}>
              <svg ref={svgRef} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} style={{ width: '100%', height: 'auto', display: 'block', touchAction: 'none', cursor: drag.current?.pan ? 'grabbing' : 'default' }}
                onPointerDown={(event) => onPointerDown(event, null)} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp} onWheel={onWheel}>
                <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`}>
                  {graph.edges.map((edge) => {
                    const a = point(edge.source);
                    const b = point(edge.target);
                    if (!a || !b) return null;
                    const label = edge.props?.name || edge.type;
                    return (
                      <g key={edge.id}>
                        <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="currentColor" strokeOpacity={edge.props?.invalid_at ? 0.15 : 0.35} strokeWidth={1.2} />
                        {showEdgeLabels ? <text x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 - 3} fontSize={9} fill="currentColor" fillOpacity={0.55} textAnchor="middle">{short(label, 28)}</text> : null}
                        <title>{edge.type}{edge.props?.fact ? `: ${edge.props.fact}` : ''}</title>
                      </g>
                    );
                  })}
                  {graph.nodes.map((node) => {
                    const p = point(node.id);
                    if (!p) return null;
                    const active = node.id === selected;
                    return (
                      <g key={node.id} transform={`translate(${p.x} ${p.y})`} style={{ cursor: 'pointer' }} onPointerDown={(event) => onPointerDown(event, node.id)}>
                        <circle r={active ? NODE_RADIUS + 3 : NODE_RADIUS} fill={colourOf(node.label)} stroke="currentColor" strokeWidth={active ? 2.5 : 0.8} strokeOpacity={active ? 0.9 : 0.4} />
                        <text x={NODE_RADIUS + 4} y={4} fontSize={11} fill="currentColor">{short(node.name || node.label)}</text>
                        <title>{node.label}: {node.name}</title>
                      </g>
                    );
                  })}
                </g>
              </svg>
            </div>
            <div className="flex flex-wrap gap-3 text-xs">
              {labels.map(([label, count]) => (
                <span key={label} className="inline-flex items-center gap-1">
                  <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: colourOf(label) }} />
                  {label} ({formatInteger(count)})
                </span>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="text-lg font-bold">{g.selected}</h3>
            {!selectedNode ? <div className="text-sm text-text-secondary">{g.selectHint}</div> : (
              <div className="space-y-3">
                <div className="text-sm">
                  <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: colourOf(selectedNode.label), marginRight: 6 }} />
                  <strong>{selectedNode.label}</strong>
                  {(selectedNode.labels || []).length > 1 ? <span className="text-text-secondary"> ({selectedNode.labels.join(', ')})</span> : null}
                </div>
                <div className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>{selectedNode.name}</div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className="homepage-button-secondary" onClick={() => expand(selected, 1)} disabled={busy}>{g.expand}</button>
                  <button type="button" className="homepage-button-secondary" onClick={() => expand(selected, 2)} disabled={busy}>{g.expandTwo}</button>
                  <button type="button" className="homepage-button-secondary" onClick={() => centre(selected)} disabled={busy}>{g.centre}</button>
                  {isEntity(selectedNode) ? <button type="button" className="homepage-button-secondary" onClick={() => loadFacts(selected)} disabled={busy}>{g.facts}</button> : null}
                </div>
                {detail ? (
                  <>
                    <div className="text-sm text-text-secondary">{g.degree}: {formatInteger(detail.degree)}</div>
                    <Table columns={[
                      { key: 'type', label: g.relationshipType },
                      { key: 'direction', label: g.direction, render: (row) => (row.direction === 'out' ? g.out : g.in) },
                      { key: 'count', label: m.count, render: (row) => formatInteger(row.count) },
                    ]} rows={(detail.degrees || []).map((row) => ({ ...row, id: `${row.type}-${row.direction}` }))} empty={g.noRelationships} />
                    <PropertyTable props={detail.props} g={g} />
                  </>
                ) : <div className="text-sm text-text-secondary">{m.loading}</div>}
              </div>
            )}
          </div>
        </div>
      </Section>

      {facts ? (
        <Section title={`${g.factsOf} ${facts.entity?.name || ''}`} subtitle={g.factsSubtitle}>
          <Table rows={facts.items || []} empty={m.noFacts} columns={[
            { key: 'relation', label: g.relation },
            { key: 'fact', label: m.fact },
            { key: 'other', label: g.other, render: (row) => <>{row.direction === 'out' ? '→' : '←'} {row.otherLabel}: {showButton(row.otherId, row.other)}</> },
            { key: 'scope', label: m.scope },
            { key: 'valid_at', label: m.validFrom, render: (row) => formatTime(row.valid_at) },
            { key: 'invalid_at', label: m.validUntil, render: (row) => (row.invalid_at ? formatTime(row.invalid_at) : m.stillValid) },
          ]} />
        </Section>
      ) : null}

      <Section id="memory-graph-newest" title={g.newestTitle} subtitle={g.newestSubtitle} count={newest.length} collapsed>
        <Table rows={newest} empty={m.empty} columns={[
          { key: 'created_at', label: m.created, render: (row) => formatTime(row.created_at) },
          { key: 'kind', label: m.kind, render: (row) => m.kinds?.[row.kind] || row.kind },
          { key: 'text', label: m.text, render: (row) => <span style={{ whiteSpace: 'pre-wrap' }}>{row.text}</span> },
          { key: 'scope', label: m.scope },
          { key: 'about', label: g.about, render: (row) => ((row.about || []).length ? row.about.join(' · ') : '-') },
          { key: 'invalid', label: m.validity, render: (row) => (row.invalid ? m.invalid : m.stillValid) },
          { key: 'show', label: '', render: (row) => showButton(row.id) },
        ]} />
      </Section>

      <Section id="memory-graph-work" title={g.workTitle} subtitle={g.workSubtitle} count={work.length} collapsed actions={(
        <form className="flex flex-wrap gap-2" onSubmit={(event) => { event.preventDefault(); loadWork(workStatus); }}>
          <select className="form-input" value={workStatus} onChange={(event) => setWorkStatus(event.target.value)}>
            <option value="">{g.allStates}</option>
            {['confirmed', 'in_progress', 'blocked', 'done', 'failed', 'dropped'].map((status) => <option key={status} value={status}>{g.states?.[status] || status}</option>)}
          </select>
          <button type="submit" className="homepage-button-secondary" disabled={busy}>{m.filter}</button>
        </form>
      )}>
        <Table rows={work} empty={m.empty} columns={[
          { key: 'workItem', label: g.workItem },
          { key: 'title', label: g.name },
          { key: 'status', label: m.status, render: (row) => g.states?.[row.status] || row.status },
          { key: 'owner', label: g.owner },
          { key: 'issue', label: g.issue },
          { key: 'scenarios', label: g.scenarios, render: (row) => ((row.scenarios || []).length ? row.scenarios.map((scenario) => `${scenario.name}: ${g.results?.[scenario.result] || scenario.result || 'pending'}`).join(' · ') : '-') },
          { key: 'updated_at', label: m.updated, render: (row) => formatTime(row.updated_at) },
          { key: 'show', label: '', render: (row) => showButton(row.id) },
        ]} />
      </Section>
    </>
  );
};

export default MemoryGraph;
